import { motion } from "framer-motion";
import { Zap, Building2, MapPin, Handshake } from "lucide-react";

const MANCHESTER_IMG =
  "https://images.pexels.com/photos/14957247/pexels-photo-14957247.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const VALUE_PROPS = [
  { icon: Zap, label: "24h Average Quote Turnaround" },
  { icon: Building2, label: "Commercial & Specialist Services" },
  { icon: MapPin, label: "Manchester & North West Coverage" },
  { icon: Handshake, label: "One Accountable Team" },
];

const PARAGRAPHS = [
  "NexaVerve Solutions was founded on a simple belief: high standards shouldn't be difficult to achieve or maintain.",
  "After spending years in hospitality and operational leadership roles — managing hotels, commercial environments, large teams and complex service operations — I saw first-hand the difference that strong standards, clear processes and accountable leadership can make. I also saw the challenges businesses face when cleaning, maintenance, compliance and operational support are managed separately.",
  "NexaVerve was created to bring these services together under one trusted partner.",
  "Our experience spans cleaning operations, facilities management, project delivery, health and safety, compliance, team leadership and customer service. We understand what clients expect because we've been responsible for delivering those standards ourselves in demanding environments where quality, consistency and accountability matter every day.",
  "Today, NexaVerve supports domestic, commercial, educational and property sector clients across Manchester and the North West, providing professional cleaning, maintenance, PAT testing, compliance support and consultancy services.",
  "Whether we're supporting a homeowner, a growing business, a property manager or a large organisation, our focus remains the same: delivering reliable service, maintaining high standards and building long-term relationships based on trust.",
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 sm:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header row */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14 sm:mb-20">
          <div className="lg:col-span-7">
            <p className="tag-label mb-5">// Section 02 / About NexaVerve</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] text-white">
              Founded in <span className="text-[#FBBF24]">2025.</span>
              <br />
              Built on <span className="italic font-medium">decades</span> of standards.
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p
              data-testid="about-lead"
              className="text-neutral-300 text-base sm:text-lg leading-relaxed"
            >
              NexaVerve Solutions was built on years of experience in
              hospitality, operations, compliance and facilities management —
              brought together under one trusted partner.
            </p>
          </div>
        </div>

        {/* Body row: image + story */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="relative lg:sticky lg:top-28">
              <img
                src={MANCHESTER_IMG}
                alt="Manchester skyline at night"
                className="w-full h-[420px] sm:h-[480px] object-cover grayscale contrast-110 border border-white/10"
              />
              <div className="absolute -bottom-px -right-px bg-[#FBBF24] text-black px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em]">
                Based in Manchester
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="space-y-6 sm:space-y-7">
              {PARAGRAPHS.map((p, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  data-testid={`about-paragraph-${i}`}
                  className={`leading-relaxed ${
                    i === 0
                      ? "text-white text-lg sm:text-xl font-medium"
                      : "text-neutral-400 text-base"
                  }`}
                >
                  {p}
                </motion.p>
              ))}

              {/* Brand tagline pull-quote */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                data-testid="about-tagline"
                className="mt-10 pt-8 border-t border-white/10"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-3">
                  Our promise
                </p>
                <p className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">
                  Excellence <span className="text-[#FBBF24]">Delivered.</span>{" "}
                  Compliance <span className="text-[#FBBF24]">Assured.</span>
                </p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Value props */}
        <div className="mt-16 sm:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-white/10">
          {VALUE_PROPS.map((v, i) => {
            const Icon = v.icon;
            return (
              <div
                key={i}
                data-testid={`value-prop-${i}`}
                className={`flex items-center gap-4 p-6 sm:p-7 ${
                  i !== 0 ? "lg:border-l border-white/10" : ""
                } ${
                  i % 2 === 1 ? "sm:border-l border-white/10" : ""
                } ${i < 2 ? "border-b sm:border-b lg:border-b-0" : ""} ${
                  i === 2 ? "sm:border-b-0" : ""
                } border-white/10`}
              >
                <Icon className="w-6 h-6 text-[#FBBF24] shrink-0" strokeWidth={1.8} />
                <span className="font-display text-base sm:text-lg font-semibold text-white leading-tight">
                  {v.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
